/**
 * ATTRACT STATE (B6): what the attract loop is showing, for <AttractOverlay/>.
 *
 * A tiny external store (not the app store: captions are presentation of the kiosk loop, not app
 * state). The loop writes it a few times per scene; the overlay reads it with useSyncExternalStore.
 */
import { useSyncExternalStore } from "react";
import type { Caption } from "./script";

export interface AttractSnapshot {
  /** The caption on screen, or null between scenes. */
  caption: Caption | null;
  /** Scene index (§G), −1 before the first scene. */
  scene: number;
  loop: number;
}

const INITIAL: AttractSnapshot = { caption: null, scene: -1, loop: 0 };
let snap: AttractSnapshot = INITIAL;
const subs = new Set<() => void>();

export const attractState = {
  get(): AttractSnapshot {
    return snap;
  },
  set(patch: Partial<AttractSnapshot>): void {
    const next = { ...snap, ...patch };
    if (next.caption === snap.caption && next.scene === snap.scene && next.loop === snap.loop) return;
    snap = next;
    for (const fn of [...subs]) fn();
  },
  reset(): void {
    if (snap === INITIAL) return;
    snap = INITIAL;
    for (const fn of [...subs]) fn();
  },
  subscribe(fn: () => void): () => void {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  },
};

export function useAttractState(): AttractSnapshot {
  return useSyncExternalStore(attractState.subscribe, attractState.get, attractState.get);
}
